import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Facebook, MessageCircle } from 'lucide-react';
import { heroSlides, companyInfo } from '../data/content';
import { Button } from './ui/button';

export const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const slide = heroSlides[currentSlide];
  const whatsappNumber = "27615290223";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hi%20Apollo%20House!%20I'm%20interested%20in%20student%20accommodation.`;

  return (
    <section data-testid="hero-slider" className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Images */}
      {heroSlides.map((s, idx) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt={`${s.tagline} ${s.subline} ${s.endline}`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      {/* Overlay with fun gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
      
      {/* Decorative Elements - Warm colors */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-coral/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange/20 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="section-container">
          <div className="max-w-3xl">
            <div
              key={currentSlide}
              className="animate-slide-in"
            >
              {/* Fun Badge */}
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-white text-sm font-medium">Now Accepting 2027 Applications!</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white font-['Outfit'] leading-tight">
                <span className="text-coral">{slide.tagline}</span>
                <br />
                <span>{slide.subline}</span>
                <br />
                <span>{slide.endline}</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-slate-200 max-w-xl">
                South Africa's leading student accommodation provider. Residences in Vanderbijlpark, Johannesburg, Pretoria & Cape Town. NSFAS accredited.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="hero-apply-btn"
                    className="bg-gradient-to-r from-coral to-orange hover:from-coral-dark hover:to-coral text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-coral/30 transition-all hover:shadow-xl hover:shadow-coral/40 hover:scale-105"
                  >
                    Apply Now
                  </Button>
                </Link>
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid="hero-whatsapp-btn"
                    className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all hover:scale-105 flex items-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp Us
                  </Button>
                </a>
                <a 
                  href={companyInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid="hero-facebook-btn"
                    className="bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all hover:scale-105 flex items-center gap-2"
                  >
                    <Facebook className="w-5 h-5" />
                    Facebook
                  </Button>
                </a>
              </div>
              
              {/* Stats Bar */}
              <div className="mt-12 flex flex-wrap gap-8">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-coral font-['Outfit']">500+</div>
                  <div className="text-slate-300 text-sm">Happy Students</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white font-['Outfit']">R4,700</div>
                  <div className="text-slate-300 text-sm">NSFAS Rate/Month</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-green-400 font-['Outfit']">24/7</div>
                  <div className="text-slate-300 text-sm">Security</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        data-testid="hero-prev-btn"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 hover:bg-coral backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        data-testid="hero-next-btn"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 hover:bg-coral backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all hover:scale-110"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (!isAnimating) {
                setIsAnimating(true);
                setCurrentSlide(idx);
                setTimeout(() => setIsAnimating(false), 600);
              }
            }}
            data-testid={`hero-indicator-${idx}`}
            className={`h-3 rounded-full transition-all ${
              idx === currentSlide
                ? 'w-10 bg-coral'
                : 'w-3 bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
      
      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] hover:bg-[#20BD5A] rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 transition-all hover:scale-110"
        data-testid="floating-whatsapp"
      >
        <MessageCircle className="w-8 h-8 text-white" />
      </a>
    </section>
  );
};
